import PropTypes from "prop-types";
import LocationIcon from "assets/images/defaultIcons/location.svg";
import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import {
  buildGoogleMapsSearchUrl,
  getRelatedAlerts,
} from "shared/helpers/shared.helpers";
import { ALERT_ACCESSOR_LOCATION } from "web/constants";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomTextEditor from "webComponentsFormElements/inputs/CustomTextEditor.component";
import FileUpload from "webComponentsFormElements/inputs/FileUpload.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import RelatedAlerts from "webComponentsShared/RelatedAlerts.component";
import { AlertContext } from "webContext/Alert.context";
import { OrganizationContext } from "webContext/Organization.context";
import PersonSelectionModal from "./PersonSelectionModal.component";

const classes = StyleSheet.create({
  rows: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    padding: 25,
    width: "100%",
  },
  centerAligned: {
    alignSelf: "center",
  },
  italicText: {
    fontStyle: "italic",
  },
  marginTop20: {
    marginTop: 20,
  },
  marginTop15: {
    marginTop: 15,
  },
  rowContainer: {
    flexDirection: "row",
    gap: 10,
    width: "100%",
  },
  flexOne: {
    flex: 1,
  },
  smallText: {
    fontSize: 12,
  },
  checkmarkText: {
    fontSize: 18,
  },
  header: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    paddingBottom: 8,
  },
  content: {
    fontWeight: theme.font.weight.regular,
    color: theme.color.darkGrey,
    paddingLeft: 24,
    paddingBottom: 8,
  },
  deletePressable: {
    position: "absolute",
    zIndex: 1,
    right: -25,
  },
  contentContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    flex: 1,
    gap: 15,
    width: "48%",
    minWidth: "300px",
  },
  input: {
    flexDirection: "column",
    maxWidth: "100%",
  },
  buttonRow: {
    flexDirection: "row",
    maxWidth: "100%",
    gap: 5,
    left: 4,
  },
  uploadStyle: {
    paddingTop: 25,
    bottom: -10,
  },
  deleteIcon: {
    color: theme.color.alertRed,
    fontSize: 20,
  },
  mapsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 100,
    width: "100%",
  },
  tooltipView: {
    zIndex: 100,
    width: "100%",
  },
  tooltipContainer: {
    color: theme.color.darkGrey,
    backgroundColor: theme.color.white,
    padding: 10,
    position: "absolute",
    right: "80%",
    width: 200,
    borderRadius: 5,
    borderTopEndRadius: 0,
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
    zIndex: 100,
  },
  wideModal: {
    width: "90%",
    maxWidth: "1200px",
    minHeight: "80vh",
  },
  associationSection: {
    marginTop: 20,
    padding: 15,
    backgroundColor: theme.color.lightGrey,
    borderRadius: 8,
  },
  associationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  associationTitle: {
    fontSize: 16,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.backgroundText,
  },
  clickToEdit: {
    fontSize: 12,
    color: theme.color.primary,
    fontStyle: "italic",
  },
  associationItem: {
    padding: 8,
    marginVertical: 2,
    backgroundColor: theme.color.white,
    borderRadius: 4,
  },
  associationName: {
    fontSize: 14,
    color: theme.color.backgroundText,
  },
  selectionModal: {
    width: "70%",
    maxWidth: "800px",
    maxHeight: "80vh",
  },
  selectionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
    marginVertical: 2,
    borderRadius: 4,
  },
});

export default function UpsertLocationForm(props) {
  const {
    setCanSave,
    location,
    setLocationToUpsert,
    setSelectedImageFile,
    previewImageUrl,
    setPreviewImageUrl,
  } = props;
  const {
    personList,
    otherContactList,
    getPersons,
    getOtherContacts,
    organizationColor,
  } = useContext(OrganizationContext);
  const { sortedAllAlerts, getAlerts } = useContext(AlertContext);

  useEffect(
    () => setPreviewImageUrl(location?.signedFileUrl || null),
    [location, setPreviewImageUrl],
  );

  const [isLoading, setIsLoading] = useState(true);

  const [fields, setFields] = useState(() => ({
    locationName: location?.name || "",
    url: location?.url || "",
    address: location?.address || "",
    addressTwo: location?.addressTwo || "",
    city: location?.city || "",
    state: location?.state || "",
    zipCode: location?.zipCode || "",
    primaryPhone: location?.primaryPhone || "",
    secondaryPhone: location?.secondaryPhone || "",
    primaryEmail: location?.primaryEmail || "",
    secondaryEmail: location?.secondaryEmail || "",
    additionalInformation: location?.additionalInformation || "",
  }));
  const setField = useCallback(
    (key) => (value) => setFields((prev) => ({ ...prev, [key]: value })),
    [],
  );
  const [hasImageChange, setHasImageChange] = useState(false);
  const [selectedPersonIds, setSelectedPersonIds] = useState(
    () => location?._associations?.persons?.map((person) => person.personId) || [],
  );
  const [selectedOtherContactIds, setSelectedOtherContactIds] = useState(
    () => location?._associations?.otherContacts?.map((oc) => oc.otherContactId) || [],
  );
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [otherContactModalOpen, setOtherContactModalOpen] = useState(false);
  const [otherContactSearchText, setOtherContactSearchText] = useState("");


  const hasChanges = useMemo(() => {
    if (!location) {
      return fields.locationName.length > 0;
    }

    const originalPersonIds =
      location._associations?.persons?.map((person) => person.personId) || [];
    const originalOtherContactIds =
      location._associations?.otherContacts?.map((oc) => oc.otherContactId) ||
      [];

    const personIdsChanged =
      JSON.stringify([...selectedPersonIds].sort()) !==
      JSON.stringify([...originalPersonIds].sort());
    const otherContactIdsChanged =
      JSON.stringify([...selectedOtherContactIds].sort()) !==
      JSON.stringify([...originalOtherContactIds].sort());

    return (
      location?.name !== fields.locationName ||
      (location?.url || "") !== fields.url ||
      (location?.address || "") !== fields.address ||
      (location?.addressTwo || "") !== fields.addressTwo ||
      (location?.city || "") !== fields.city ||
      (location?.state || "") !== fields.state ||
      (location?.zipCode || "") !== fields.zipCode ||
      (location?.primaryPhone || "") !== fields.primaryPhone ||
      (location?.secondaryPhone || "") !== fields.secondaryPhone ||
      (location?.primaryEmail || "") !== fields.primaryEmail ||
      (location?.secondaryEmail || "") !== fields.secondaryEmail ||
      (location?.additionalInformation || "") !== fields.additionalInformation ||
      hasImageChange ||
      personIdsChanged ||
      otherContactIdsChanged
    );
  }, [
    location,
    fields,
    hasImageChange,
    selectedPersonIds,
    selectedOtherContactIds,
  ]);

  const newLocationIsValid = useMemo(() => {
    const isNewLocation = location === null;
    return isNewLocation && fields.locationName.length > 0;
  }, [location, fields.locationName]);

  useEffect(() => {
    if (hasChanges || newLocationIsValid) {
      setLocationToUpsert({
        locationId: location?.locationId || null,
        name: fields.locationName,
        url: buildGoogleMapsSearchUrl(fields.url),
        address: fields.address,
        addressTwo: fields.addressTwo,
        city: fields.city,
        state: fields.state,
        zipCode: fields.zipCode,
        primaryPhone: fields.primaryPhone,
        secondaryPhone: fields.secondaryPhone,
        primaryEmail: fields.primaryEmail,
        secondaryEmail: fields.secondaryEmail,
        additionalInformation: fields.additionalInformation,
        personIds: selectedPersonIds,
        otherContactIds: selectedOtherContactIds,
      });
    }
  }, [
    hasChanges,
    newLocationIsValid,
    setLocationToUpsert,
    location,
    fields,
    selectedPersonIds,
    selectedOtherContactIds,
  ]);

  useEffect(() => {
    setCanSave(hasChanges || newLocationIsValid);
  }, [hasChanges, newLocationIsValid, setCanSave]);

  useEffect(() => {
    if (!personList || personList.length === 0) {
      getPersons();
    }
  }, [personList, otherContactList, getPersons, getOtherContacts]);

  useEffect(() => {
    if (!sortedAllAlerts || sortedAllAlerts.length === 0) {
      getAlerts();
    }
  }, [getAlerts, sortedAllAlerts]);

  const relatedAlerts = useMemo(
    () =>
      getRelatedAlerts(
        ALERT_ACCESSOR_LOCATION,
        location?.locationId,
        sortedAllAlerts,
      ),
    [sortedAllAlerts, location?.locationId],
  );

  const togglePerson = useCallback((personId) => {
    setSelectedPersonIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  }, []);

  const toggleOtherContact = useCallback((otherContactId) => {
    setSelectedOtherContactIds((prev) =>
      prev.includes(otherContactId)
        ? prev.filter((id) => id !== otherContactId)
        : [...prev, otherContactId],
    );
  }, []);

  const filteredOtherContacts = useMemo(() => {
    return (
      otherContactList
        ?.filter((contact) =>
          contact.name
            .toLowerCase()
            .includes(otherContactSearchText.toLowerCase()),
        )
        .sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [otherContactList, otherContactSearchText]);

  const selectedPersons = useMemo(() => {
    return (
      personList?.filter((person) =>
        selectedPersonIds.includes(person.personId),
      ) || []
    );
  }, [personList, selectedPersonIds]);

  const selectedOtherContacts = useMemo(() => {
    return (
      otherContactList?.filter((contact) =>
        selectedOtherContactIds.includes(contact.otherContactId),
      ) || []
    );
  }, [otherContactList, selectedOtherContactIds]);

  return (
    <View>
      <View style={classes.rows}>
        <View style={classes.contentContainer}>
          <LabelInput
            label={location ? "Edit Name" : "Create Name"}
            placeholder="Place name"
            value={fields.locationName}
            onChangeText={setField("locationName")}
            required={!location}
          />
          <View style={[classes.uploadStyle, classes.centerAligned]}>
            <Text style={classes.header}>Location Image</Text>
            <FileUpload
              placeholderIcon={LocationIcon}
              file={previewImageUrl}
              setFile={setPreviewImageUrl}
              setFileBlob={setSelectedImageFile}
              sizeMultiplier={{ width: 2, height: 2 }}
              onChange={() => {
                (setHasImageChange(true), setCanSave(true));
              }}
            />
          </View>
          <View style={classes.associationSection}>
            <PressableContainer
              style={classes.associationHeader}
              onPress={() => setPersonModalOpen(true)}
            >
              <Text style={classes.associationTitle}>
                Associated Persons ({selectedPersons.length})
              </Text>
              <Text style={classes.clickToEdit}>Click to edit</Text>
            </PressableContainer>
            {selectedPersons.map((person) => (
              <View key={person.personId} style={classes.associationItem}>
                <Text style={classes.associationName}>
                  {person.firstName} {person.lastName} -{" "}
                  {person.position || "No position"}
                </Text>
              </View>
            ))}
            {selectedPersons.length === 0 && (
              <Text style={[classes.associationName, classes.italicText]}>
                No persons associated
              </Text>
            )}
          </View>

          <View style={classes.associationSection}>
            <PressableContainer
              style={classes.associationHeader}
              onPress={() => setOtherContactModalOpen(true)}
            >
              <Text style={classes.associationTitle}>
                Associated Other Contacts ({selectedOtherContacts.length})
              </Text>
              <Text style={classes.clickToEdit}>Click to edit</Text>
            </PressableContainer>
            {selectedOtherContacts.map((contact) => (
              <View
                key={contact.otherContactId}
                style={classes.associationItem}
              >
                <Text style={classes.associationName}>
                  {contact.name} - {contact.organization || "No organization"}
                </Text>
              </View>
            ))}
            {selectedOtherContacts.length === 0 && (
              <Text
                style={[
                  classes.associationName,
                  classes.italicText,
                  { color: theme.color.mediumGrey },
                ]}
              >
                No other contacts associated
              </Text>
            )}
          </View>
          {location && (
            <RelatedAlerts alerts={relatedAlerts} entityType="Location" />
          )}
          <View style={classes.marginTop20}>
            <Text style={classes.header}>Additional Information</Text>
            {isLoading && <ActivityIndicator size="large" />}
            <CustomTextEditor
              initialText={fields.additionalInformation}
              isLoadingCallback={setIsLoading}
              border
              onChangeText={setField("additionalInformation")}
            />
          </View>
        </View>
        <View style={classes.contentContainer}>
          <LabelInput
            label="Address"
            placeholder="Street address"
            value={fields.address}
            onChangeText={setField("address")}
          />
          <LabelInput
            label="Address Line 2"
            placeholder="Apt, Suite, etc."
            value={fields.addressTwo}
            onChangeText={setField("addressTwo")}
          />
          <LabelInput
            label="City"
            placeholder="City"
            value={fields.city}
            onChangeText={setField("city")}
          />
          <View style={classes.rowContainer}>
            <View style={classes.flexOne}>
              <LabelInput
                label="State"
                placeholder="State"
                value={fields.state}
                onChangeText={setField("state")}
              />
            </View>
            <View style={classes.flexOne}>
              <LabelInput
                label="Zip Code"
                placeholder="Zip"
                value={fields.zipCode}
                onChangeText={setField("zipCode")}
                numbersOnly
              />
            </View>
          </View>
          <LabelInput
            label="Primary Phone"
            placeholder="Primary phone number"
            value={fields.primaryPhone}
            onChangeText={setField("primaryPhone")}
            isPhone
          />
          <LabelInput
            label="Primary Email"
            placeholder="Primary email address"
            value={fields.primaryEmail}
            onChangeText={setField("primaryEmail")}
          />
          <LabelInput
            label="Secondary Phone"
            placeholder="Secondary phone number"
            value={fields.secondaryPhone}
            onChangeText={setField("secondaryPhone")}
            isPhone
          />
          <LabelInput
            label="Secondary Email"
            placeholder="Secondary email address"
            value={fields.secondaryEmail}
            onChangeText={setField("secondaryEmail")}
          />
          <LabelInput
            label="Google Maps Address"
            placeholder="100 middle street portland me OR https://maps.google.com/..."
            value={fields.url}
            onChangeText={setField("url")}
            multiline
          />
        </View>
      </View>
      <PersonSelectionModal
        open={personModalOpen}
        onClose={() => setPersonModalOpen(false)}
        personList={personList}
        selectedPersonIds={selectedPersonIds}
        onTogglePerson={togglePerson}
        organizationColor={organizationColor}
        title="Select Persons"
      />
      <CustomModal
        open={otherContactModalOpen}
        onClose={() => {
          setOtherContactModalOpen(false);
          setOtherContactSearchText("");
        }}
        title="Select Other Contacts"
        style={classes.selectionModal}
        scroll
      >
        <View>
          <SearchBar
            value={otherContactSearchText}
            onChangeText={setOtherContactSearchText}
          />
          <View style={classes.marginTop15}>
            {filteredOtherContacts.map((contact) => (
              <PressableContainer
                key={contact.otherContactId}
                style={classes.selectionRow}
                onPress={() => toggleOtherContact(contact.otherContactId)}
              >
                <View>
                  <Text style={classes.associationName}>{contact.name}</Text>
                  <Text
                    style={[
                      classes.associationName,
                      classes.smallText,
                      { color: theme.color.mediumGrey },
                    ]}
                  >
                    {contact.organization || "No organization"} •{" "}
                    {contact.email}
                  </Text>
                </View>
                <Text
                  style={[classes.checkmarkText, { color: organizationColor }]}
                >
                  {selectedOtherContactIds.includes(contact.otherContactId)
                    ? "✓"
                    : "+"}
                </Text>
              </PressableContainer>
            ))}
          </View>
        </View>
      </CustomModal>
    </View>
  );
}

  UpsertLocationForm.propTypes = {
    location: PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      address: PropTypes.string,
      addressTwo: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
      primaryPhone: PropTypes.string,
      secondaryPhone: PropTypes.string,
      primaryEmail: PropTypes.string,
      secondaryEmail: PropTypes.string,
      additionalInformation: PropTypes.string,
      _associations: PropTypes.shape({
        persons: PropTypes.array,
        otherContacts: PropTypes.array,
      }),
    }).isRequired,
  };
